<template>
	<article class="view-content-article" v-bind:key="contentModel.uniqueKey">
		<!-- Hero imagery -->
		<section class="hero-image" v-if="hasThumbnail">
			<img v-bind:alt="title" v-bind:src="thumbnailUrl" />
		</section>

		<header class="article-header">
			<h3 class="title">{{ title }}</h3>

			<div class="meta">
				<!-- <span
					class="badge"
					v-bind:class="getBadgeClass(tagModel)"
					v-bind:key="tagModel.id"
					v-for="tagModel in contentModel.tags.models"
				>
					{{ tagModel.getTag() }}
				</span> -->

				<span class="date">{{ publishedAt }}</span>
			</div>
		</header>

		<!-- Video embed -->
		<section
			class="video-embed"
			v-bind:key="contentModel.uniqueKey"
			v-if="hasVideoEmbed"
		>
			<div class="video-container">
				<iframe allowfullscreen frameborder="0" v-bind:src="youTubeEmbedUrl"></iframe>
			</div>
		</section>

		<!-- Article content (HTML from Medium sync) -->
		<section
			class="article-body"
			v-bind:key="contentModel.uniqueKey"
			v-html="articleContent"
			v-if="hasArticleContent"
		></section>

		<!-- Fallback for video-only items with no body content -->
		<section
			class="article-body"
			v-bind:key="contentModel.uniqueKey"
			v-if="showVideoFallback"
		>
			<p>
				<a v-bind:href="mediaUrl" target="_blank" rel="noopener noreferrer">
					Watch on YouTube
				</a>
			</p>
		</section>
	</article>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';

	/**
	 * Full article/video display component for a single content item.
	 * Renders the title, meta info, YouTube embed, and HTML body.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Content
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewContentArticle extends ViewBase {
		/**
		 * Trimmed, thumbnail-stripped article HTML body.
		 *
		 * @return string
		 */
		protected get articleContent(): string {
			// Touch uniqueKey so Vue tracks reactivity on model updates
			void this.contentModel.uniqueKey;

			let content = (this.contentModel.getContent() || '') as string;

			content = content ? content.trim() : '';
			content = content.replace(this.thumbnailUrl, '');

			return content && content.length ? content : '';
		}

		/**
		 * True when the article body has renderable HTML.
		 *
		 * @return boolean
		 */
		protected get hasArticleContent(): boolean {
			return this.articleContent.length > 0;
		}

		/**
		 * True when the content model has a thumbnail URL.
		 *
		 * @return boolean
		 */
		protected get hasThumbnail(): boolean {
			return this.thumbnailUrl.length > 0;
		}

		/**
		 * True when the content is a video AND has a YouTube embed URL.
		 *
		 * @return boolean
		 */
		protected get hasVideoEmbed(): boolean {
			void this.contentModel.uniqueKey;

			return this.contentModel.isVideo() && !!this.contentModel.getYouTubeEmbed();
		}

		/**
		 * Returns the external media URL for the current content model.
		 *
		 * @return string
		 */
		protected get mediaUrl(): string {
			void this.contentModel.uniqueKey;

			return this.contentModel.getMediaUrl() || '';
		}

		/**
		 * Returns the formatted published-at date for display.
		 *
		 * @return string
		 */
		protected get publishedAt(): string {
			void this.contentModel.uniqueKey;

			return this.contentModel.getPublishedAt('MMM D, YYYY');
		}

		/**
		 * True when we should render the "Watch on YouTube" fallback
		 * (video content with no body copy).
		 *
		 * @return boolean
		 */
		protected get showVideoFallback(): boolean {
			return !this.hasArticleContent && this.contentModel.isVideo();
		}

		/**
		 * Returns the thumbnail URL for the current content model, if available
		 *
		 * @return string
		 */
		protected get thumbnailUrl(): string {
			void this.contentModel.uniqueKey;

			return this.contentModel.getThumbnailUrl() || '';
		}

		/**
		 * Returns the article title.
		 *
		 * @return string
		 */
		protected get title(): string {
			void this.contentModel.uniqueKey;

			return this.contentModel.getTitle() || '';
		}

		/**
		 * Returns the YouTube embed URL for the content, if any.
		 *
		 * @return string
		 */
		protected get youTubeEmbedUrl(): string {
			void this.contentModel.uniqueKey;

			return this.contentModel.getYouTubeEmbed() || '';
		}

		/**
		 * Content model to render
		 *
		 * @type ChalkySticks.Model.Content
		 */
		@Prop({
			required: true,
		})
		public contentModel!: ChalkySticks.Model.Content;

		/**
		 * Map of tag names to badge CSS modifier classes
		 *
		 * @type Record<string, string>
		 */
		protected badgeClassMap: Record<string, string> = {
			news: 'type-primary',
			review: 'type-warning',
			tutorial: 'type-success',
			youtube: 'type-danger',
		};

		/**
		 * Returns the badge modifier class for a given tag model
		 *
		 * @param ChalkySticks.Model.ContentTag tagModel
		 * @return string
		 */
		protected getBadgeClass(tagModel: ChalkySticks.Model.ContentTag): string {
			const tag = tagModel.getTag();

			return this.badgeClassMap[tag] || 'type-info';
		}
	}
</script>

<style lang="scss">
	.view-content-article {
		margin: 0 auto;
		max-width: 800px;
		padding: 1.5rem var(--padding-body);

		.hero-image {
			margin-bottom: 1rem;
		}

		.article-header {
			padding: 0 1rem;
			margin-bottom: 1.5rem;

			.date {
				color: var(--chalky-grey-2);
				font-size: 0.9rem;
			}

			.badge {
				text-transform: capitalize;
			}
		}

		.article-body {
			padding: 1rem;

			p {
				color: #ccc;
				font-size: 1.125rem;
				line-height: 1.5;
			}
		}

		.video-embed {
			margin-bottom: 1.5rem;

			.video-container {
				aspect-ratio: 16 / 9;
				position: relative;
				width: 100%;

				iframe {
					border-radius: 0.5rem;
					height: 100%;
					left: 0;
					position: absolute;
					top: 0;
					width: 100%;
				}
			}
		}

		// Medium fixes
		p:has(br:first-child:last-child) {
			display: none;
		}

		.article-body h3::before {
			border-bottom: 1px solid var(--chalky-grey-4);
			content: ' ';
			display: block;
			height: 1px;
			margin: 2rem 0;
			width: 100%;
		}

		figure {
			margin: 1rem 0 1.5rem 0;

			img {
				border-radius: 0.5em;
			}
		}

		a[href*="https://www.chalkysticks.com"]
		{
			display: none;
		}
	}

	// Media Query
	// ---------------------------------------------------------------------------

	@include media-query('<= tablet') {
		.view-content-article {
			.article-header .title {
				font-size: 1.35rem;
			}
		}
	}
</style>
